import { useApi } from '@backstage/core-plugin-api';
import { useCallback, useEffect, useState } from 'react';
import {
  AiAssistantsCatalogApi,
  aiAssistantsCatalogApiRef,
  AssistantRecord,
} from '../../../apis/aiAssistantsCatalogApi';
import {
  NotificationApi,
  notificationApiRef,
} from '../../../apis/notificationApi';
import { AgentCardProps } from '../components/AgentCard';

const DEFAULT_BADGE_COLOR = '#059669';

const normalizeStatus = (
  value?: string,
): 'Approved' | 'Under Review' | 'Rejected' | 'Resubmit' => {
  const normalized = (value || '').toLowerCase();
  if (normalized === 'approved') {
    return 'Approved';
  }
  if (normalized === 'rejected') {
    return 'Rejected';
  }
  if (normalized === 'resubmit') {
    return 'Resubmit';
  }
  return 'Under Review';
};

const formatDate = (value?: string) => {
  if (!value) {
    return 'Recently Updated';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Recently Updated';
  }

  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  });
};

const extractDataNode = (input: any): any => {
  if (input == null) {
    return input;
  }
  if (Array.isArray(input)) {
    return input;
  }
  if (Array.isArray(input?.data)) {
    return input.data;
  }
  if (input?.data && typeof input.data === 'object') {
    return extractDataNode(input.data);
  }
  if (Array.isArray(input?.items)) {
    return input.items;
  }
  if (Array.isArray(input?.results)) {
    return input.results;
  }
  if (input?.result && typeof input.result === 'object') {
    return extractDataNode(input.result);
  }
  return input;
};

const extractAssistantList = (input: any): AssistantRecord[] => {
  const data = extractDataNode(input);
  if (Array.isArray(data)) {
    return data as AssistantRecord[];
  }
  if (data && typeof data === 'object' && (data.id || data.name)) {
    return [data as AssistantRecord];
  }
  return [];
};

const extractBookmarkIds = (input: any): Set<string> => {
  const data = extractDataNode(input);
  if (!Array.isArray(data)) {
    return new Set<string>();
  }

  const ids = data
    .map(item => {
      if (typeof item === 'string') {
        return item;
      }
      return item?.assistantId || item?.assistantid || item?.id;
    })
    .filter((value): value is string => typeof value === 'string' && !!value);

  return new Set(ids);
};

export const extractAssistantId = (input: any): string | undefined => {
  const data = extractDataNode(input);
  if (typeof data?.id === 'string') {
    return data.id;
  }
  if (typeof input?.id === 'string') {
    return input.id;
  }
  return undefined;
};

const toAgentCard = (
  assistant: AssistantRecord,
  bookmarkIds: Set<string>,
): AgentCardProps => {
  const targetAudience = (assistant.targetAudience as any) || {};
  const governance = (assistant.governance as any) || {};
  const evidence = (assistant.evidence as any) || {};
  const integrationMethod = (assistant.integrationMethod as any) || {};
  const agentConfiguration = (assistant.agentConfiguration as any) || {};
  const interactionSettings = (assistant.interactionSettings as any) || {};
  const status = normalizeStatus(assistant.approvalStatus || assistant.status);
  const assistantId = assistant.id;
  const createdBy = assistant.createdBy || assistant.createdby;
  const ownerNameRaw = assistant.owner?.name;
  const normalizedOwnerName = ownerNameRaw?.trim();
  const ownerName =
    normalizedOwnerName && normalizedOwnerName.toLowerCase() !== 'unknown'
      ? normalizedOwnerName
      : createdBy || 'Unknown';
  const ownerEmail = assistant.owner?.email || createdBy;
  const useCases = (assistant.useCases || []).map(useCase => ({
    name: useCase,
    color: DEFAULT_BADGE_COLOR,
  }));

  return {
    id: assistantId,
    title: assistant.name || 'Untitled Assistant',
    description: assistant.description || '',
    avatarColor: assistant.avatar?.color || '#4A7BA7',
    useCases,
    agentType: assistant.assistantType || 'Personal Assistant',
    agentTypeColor: DEFAULT_BADGE_COLOR,
    owner: ownerName,
    ownerEmail,
    createdBy,
    segment: assistant.segment,
    aiCategory: assistant.aiCategory,
    approvalStatus: status,
    updatedTime: formatDate(
      assistant.updatedAt ||
        assistant.updatedat ||
        assistant.createdAt ||
        assistant.createdat,
    ),
    bookmarked:
      typeof assistant.bookmarked === 'boolean'
        ? assistant.bookmarked
        : !!assistantId && bookmarkIds.has(assistantId),
    modalContent: {
      overview: assistant.overview ? [assistant.overview] : assistant.description ? [assistant.description] : [],
      capabilities: assistant.capabilities || [],
      targetAudience: {
        designedFor:
          targetAudience.designedfor ||
          targetAudience.designedFor ||
          assistant.owner?.name ||
          '',
        businessGroup:
          targetAudience.businessgroup ||
          targetAudience.businessGroup ||
          assistant.lob ||
          '',
        geography: targetAudience.geography || '',
        geographicDetails:
          targetAudience.specificlocation || targetAudience.specificLocation,
        segment: assistant.segment,
        lob: assistant.lob,
        aiCategory: assistant.aiCategory,
      },
      whatYouProvide: assistant.whatYouProvide || [],
      whatYouGet: assistant.whatYouGet || [],
      governance: {
        dataAccess: governance.dataaccess || governance.dataAccess || '',
        compliance:
          governance.compliance ||
          (status === 'Approved' ? 'Approved' : 'Under Review'),
        validationSummary:
          governance.validationsummary ||
          governance.validationSummary ||
          assistant.comments ||
          '',
        validatedBy: governance.validatedby || governance.validatedBy || 'AI Governance',
        version: governance.version || 'v1.0',
        importantToKnow:
          governance.importanttoknow ||
          governance.importantToKnow ||
          'Validate assistant outputs before operational decisions.',
        evidenceLink: evidence.link,
        evidenceFileName: evidence.filename,
        evidenceFilePath: evidence.filepath,
        evidenceFileType: evidence.filetype,
        evidenceFileSize: evidence.filesize,
      },
      integrationMethod: {
        method: integrationMethod.method,
        configuration: integrationMethod.configuration,
      },
      agentConfiguration: {
        agentUrl: agentConfiguration.agentUrl,
        agentVersion: agentConfiguration.agentVersion,
        skills: agentConfiguration.skills || [],
        systemCapabilities: agentConfiguration.systemCapabilities || [],
      },
      interactionSettings: {
        inputFormat: interactionSettings.inputFormat,
        responseFormat: interactionSettings.responseFormat,
        requiresSecureAccess: !!interactionSettings.requiresSecureAccess,
      },
      releaseNotes: (assistant.releaseNotes || []).map(note => ({
        version: note.version,
        notes:
          note.changes && note.changes.length
            ? note.changes
            : [note.summary || 'No release notes details'],
      })),
    },
  };
};

export const useAiStoreAssistants = () => {
  const assistantsApi: AiAssistantsCatalogApi = useApi(aiAssistantsCatalogApiRef);
  const notificationApi: NotificationApi = useApi(notificationApiRef);

  const [agents, setAgents] = useState<AgentCardProps[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const refetchAssistants = useCallback(async () => {
    setLoading(true);
    try {
      const [assistantsResponse, bookmarksResponse] = await Promise.all([
        assistantsApi.getAllAssistants(),
        assistantsApi.getUserBookmarks(),
      ]);

      const bookmarks = extractBookmarkIds(bookmarksResponse);
      const mappedAgents = extractAssistantList(assistantsResponse).map(assistant =>
        toAgentCard(assistant, bookmarks),
      );

      setAgents(mappedAgents);
      return mappedAgents;
    } catch (error: any) {
      notificationApi.sendNotification({
        message: error?.message || 'Failed to load AI assistants',
        severity: 'error',
      });
      return [] as AgentCardProps[];
    } finally {
      setLoading(false);
    }
  }, [assistantsApi, notificationApi]);

  useEffect(() => {
    void refetchAssistants();
  }, [refetchAssistants]);

  const getAssistantDetailsById = useCallback(
    async (assistantId: string) => {
      const detailResponse = await assistantsApi.getAssistantById(assistantId);
      const detailedAssistant = extractAssistantList(detailResponse)[0];

      if (!detailedAssistant) {
        throw new Error('Assistant details were not returned');
      }

      return toAgentCard(detailedAssistant, new Set<string>());
    },
    [assistantsApi],
  );

  return {
    agents,
    setAgents,
    loading,
    refetchAssistants,
    getAssistantDetailsById,
  };
};
